import type { ChangeEvent, FC, FocusEvent, MouseEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';

import AuthError from 'src/components/account/LoginPage/components/AuthError';

import { styles } from './index.styles';

export interface LoginFormProps {
  values: { email: string; password: string };
  errors: {
    email?: string;
    password?: string;
    nonFieldErrors?: [string];
    authError?: boolean;
  };
  handleBlur: (e: FocusEvent) => void;
  handleChange: (e: ChangeEvent) => void;
  handleSubmit: () => void;
  isSubmitting: boolean;
  touched: { email?: boolean; password?: boolean };
}

const LoginForm: FC<LoginFormProps> = ({
  errors,
  handleBlur,
  handleChange,
  handleSubmit,
  isSubmitting,
  touched,
  values,
}) => {
  const { t } = useTranslation('login');
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowPassword((show) => !show);
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        placeholder={t('email')}
        type="email"
        name="email"
        autoComplete="email"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.email}
        sx={styles.inputField}
        helperText={(touched.email && errors.email) || <>&nbsp;</>}
        error={touched.email && !!errors.email}
        required
      />
      <TextField
        placeholder={t('password')}
        type={showPassword ? 'text' : 'password'}
        name="password"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.password}
        sx={styles.inputField}
        helperText={(touched.password && errors.password) || <>&nbsp;</>}
        error={touched.password && !!errors.password}
        required
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
                edge="end"
                sx={styles.adornmentButton}
              >
                {showPassword ? (
                  <VisibilityOutlined sx={styles.visibilityIcon} />
                ) : (
                  <VisibilityOffOutlined sx={styles.visibilityOffIcon} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Box sx={styles.loadingButtonWrapper}>
        <LoadingButton
          loading={isSubmitting}
          onClick={handleSubmit}
          type="submit"
          variant="cta"
          fullWidth
        >
          {t('login')}
        </LoadingButton>
      </Box>
      <Typography sx={styles.errorMsg}>
        {errors.authError ? <AuthError showAuthError /> : errors.nonFieldErrors}
      </Typography>
    </form>
  );
};

export default LoginForm;
